import { isNumber } from 'lodash'
import {
  findSmartPredictStatus,
  getSmartPredictStates
} from '../constants/smart-predict'
import {
  ASSESSMENT_SPACE_STATES,
  SMART_PREDICT_SPACE_STATES
} from '../constants/space-states'
import {
  findCustomStatus,
  isCustomStatusId,
  readTalentStatusMeta,
  toSnapshotStatusOption,
  toStatusOption
} from './talent-statuses'

const SIX_MONTH_BACK = Date.now() - 1000 * 60 * 60 * 24 * 30 * 6

const SPACE_STATES = {
  ...ASSESSMENT_SPACE_STATES,
  ...SMART_PREDICT_SPACE_STATES
}

// Die 5 Aivy-Status, die Nutzer setzen (INVITED_NEXT_ROUND, HIRED, ...).
const USER_SET_STATUS_IDS = getSmartPredictStates().map(({ id }) => id)

// application_status-Werte, die „abgeschlossen, noch keine Entscheidung" heißen.
const OPEN_STATUS_IDS = [
  'ASSESSMENT_COMPLETED',
  'INTEGRATION_SUCCESS',
  'INTEGRATION_ERROR'
]

// Immer frisch bauen: die Objekte werden von Konsumenten erweitert (action),
// ein geteiltes Modul-Objekt würde dabei mutiert.
const emptyState = () => ({
  key: 'EMPTY_STATE',
  status: { filterValue: 'empty', sortWeight: '9', color: 'grey' }
})

export const getSpacesTableData = ({
  spaces = [],
  user_id,
  customStatuses = []
}) => {
  const customOptions = (customStatuses || []).map((definition, index) =>
    toStatusOption(definition, index)
  )

  return spaces.map((space) =>
    mapSpaceForSpacesTable({ space, user_id, customOptions })
  )
}

const mapSpaceForSpacesTable = ({ space, user_id, customOptions }) => {
  const result = { ...space }

  const {
    id,
    partner_user_id,
    person,
    context,
    career_id,
    favored_by_user_id
  } = space

  const talent_status_meta = readTalentStatusMeta(space.customer_dashboard_meta)

  const smart_predict_status = getSmartPredictStatus({
    space,
    talent_status_meta,
    customOptions
  })
  const assessment_status = getAssessmentStatus({ space, talent_status_meta })

  result.score = getScore(space)
  result.assessment_status = assessment_status
  result.smart_predict_status = smart_predict_status
  result.talent_status_meta = talent_status_meta
  result.status = { assessment_status, smart_predict_status }
  result.identification = partner_user_id || id
  result.via = 'LINK'

  if (context === 'SELECT') result.assessment_id = career_id
  else if (context === 'ATTRACT') result.assessment_id = 'ATTRACT'
  else if (context === 'TALENTPOOL') result.assessment_id = 'TALENTPOOL'

  result.created_status = {
    createdAt: space.createdAt,
    wasCreatedBefore6Month: Date.parse(space.createdAt) < SIX_MONTH_BACK
  }

  result.favored_by = {
    isFavored: !!favored_by_user_id?.includes(user_id),
    action: 'updateFavoredByUserId'
  }

  result.more = [
    result.favored_by.isFavored && 'IS_FAVORED',
    result.created_status.wasCreatedBefore6Month && 'BEFORE_6_MONTH'
  ].filter((item) => item)

  if (!person) return result

  // pbc-space could come with for example person: { firstname: 'Max' }
  const space_has_email = !!(person.email || person.invitation_email)

  result.firstname = person.firstname || person.invitation_firstname
  result.lastname = person.lastname || person.invitation_lastname

  const { firstname, lastname } = result
  const email = person.email || person.invitation_email

  result.email = email
  result.phone = person.phone
  result.via = space_has_email ? 'EMAIL' : 'LINK'
  result.name = getName(firstname, lastname)
  result.initials = getInitials(result.name)
  result.person = space.person

  // Prioritize name over email for identification (used in team analysis etc.)
  result.identification =
    result.name || email || partner_user_id || id || 'Datensatz unvollständig'

  return result
}

/**
 * Talent-Status (vom Nutzer gesetzt). Reihenfolge der Quellen:
 * 1. Meta-Eintrag TALENT_STATUS (seit eigene Status möglich sind, wird er bei
 *    jedem Setzen geschrieben und gewinnt gegenüber application_status, das
 *    vom Backend überschrieben werden kann).
 * 2. Altdaten: application_status trägt den Aivy-Status. Zählt nur bei
 *    abgeschlossenem Assessment als gesetzt, so wie die Anzeige bisher war.
 * 3. Abgeschlossen ohne Entscheidung → „Offen" mit Aufforderung.
 * 4. Sonst kein Talent-Status (EMPTY_STATE).
 */
const getSmartPredictStatus = ({
  space,
  talent_status_meta,
  customOptions
}) => {
  const { application_status, progress } = space

  if (talent_status_meta) {
    const { status: id, label } = talent_status_meta

    if (USER_SET_STATUS_IDS.includes(id)) {
      return {
        key: 'SMART_PREDICT',
        status: { ...findSmartPredictStatus(id) },
        isUserStatus: true
      }
    }

    if (isCustomStatusId(id)) {
      const custom = findCustomStatus(customOptions, id)

      return {
        key: 'SMART_PREDICT',
        // Nicht mehr im Katalog: Label-Snapshot aus dem Meta-Eintrag zeigen,
        // statt das Talent still auf „Offen" zurückfallen zu lassen.
        status: custom ? { ...custom } : toSnapshotStatusOption(id, label),
        isUserStatus: true
      }
    }
  }

  if (USER_SET_STATUS_IDS.includes(application_status) && progress === 100) {
    return {
      key: 'SMART_PREDICT',
      status: { ...findSmartPredictStatus(application_status) },
      isUserStatus: true
    }
  }

  if (progress === 100 || OPEN_STATUS_IDS.includes(application_status)) {
    const open_id = OPEN_STATUS_IDS.includes(application_status)
      ? application_status
      : 'ASSESSMENT_COMPLETED'

    return {
      key: 'SMART_PREDICT',
      status: { ...findSmartPredictStatus(open_id) },
      isUserStatus: false,
      action: 'setSpaceForStateCTA'
    }
  }

  return emptyState()
}

const getAssessmentStatus = ({ space, talent_status_meta }) => {
  const { progress, person, expiresAt } = space
  let { application_status } = space

  const space_has_email = !!(person?.email || person?.invitation_email)

  // Die helperControl-Lambda schreibt Aivy-Status (HIRED, REJECTED, ...) in
  // application_status und überschreibt damit den Assessment-Stand. Den
  // vorherigen Wert trägt der Meta-Eintrag (nur bei unvollständigem
  // Assessment gespeichert). Ohne Meta gilt der alte Invariant, dass ein
  // Nutzer-Status nur nach Abschluss gesetzt werden konnte. Mit Meta, aber
  // ohne gesicherten Wert entscheidet unten der Fortschritt.
  if (USER_SET_STATUS_IDS.includes(application_status)) {
    if (talent_status_meta?.application_status_before) {
      application_status = talent_status_meta.application_status_before
    } else if (!talent_status_meta) {
      application_status = 'ASSESSMENT_COMPLETED'
    } else {
      application_status = undefined
    }
  }

  if (application_status === 'INTEGRATION_SUCCESS') {
    return {
      key: 'INTEGRATION_SUCCESS',
      status: SPACE_STATES.INTEGRATION_SUCCESS
    }
  }

  if (application_status === 'INTEGRATION_ERROR') {
    return {
      key: 'INTEGRATION_ERROR',
      status: SPACE_STATES.INTEGRATION_ERROR
    }
  }

  // SOFT_DELETED spaces are at /recover/talents

  if (application_status === 'RECOVERED') {
    return {
      key: 'RECOVERED',
      status: SPACE_STATES.RECOVERED
    }
  }

  if (application_status === 'ASSESSMENT_COMPLETED' || progress === 100) {
    return {
      key: 'ASSESSMENT_COMPLETED',
      status: SPACE_STATES.ASSESSMENT_COMPLETED
    }
  }

  if (application_status === 'EMAIL_BOUNCED') {
    return {
      key: 'EMAIL_BOUNCED',
      status: SPACE_STATES.EMAIL_BOUNCED
    }
  }

  if (expiresAt && new Date() > new Date(expiresAt)) {
    return {
      key: space_has_email ? 'EXPIRED_VIA_EMAIL' : 'EXPIRED_VIA_LINK',
      status: SPACE_STATES.EXPIRED
    }
  }

  if (application_status === 'REMINDED') {
    return {
      key: 'REMINDED',
      status: SPACE_STATES.REMINDED
    }
  }

  if (application_status === 'RESET_EXPIRED') {
    return {
      key: space_has_email
        ? 'RESET_EXPIRED_VIA_EMAIL'
        : 'RESET_EXPIRED_VIA_LINK',
      status: SPACE_STATES.RESET_EXPIRED
    }
  }

  if (application_status === 'ASSESSMENT_STARTED' || progress > 0) {
    return {
      key: space_has_email ? 'STARTED_VIA_EMAIL' : 'STARTED_VIA_LINK',
      status: { progress, ...SPACE_STATES.ASSESSMENT_STARTED }
    }
  }

  return {
    key: space_has_email ? 'INVITED_VIA_EMAIL' : 'INVITED_VIA_LINK',
    status: SPACE_STATES.TALENT_INVITED
  }
}

// Assessment-Zustände, in denen nichts mehr von sich aus passiert. Hier lohnt
// die Aufforderung, einen Talent-Status zu hinterlegen.
const FINAL_ASSESSMENT_KEYS = [
  'EXPIRED_VIA_EMAIL',
  'EXPIRED_VIA_LINK',
  'EMAIL_BOUNCED'
]

/**
 * Welcher Status wird für ein Talent primär angezeigt (Tabelle, CSV,
 * Detailseite)? Ein gesetzter Talent-Status gewinnt immer. Sonst bei
 * abgeschlossenem Assessment die Aufforderung „Status hinterlegen" (cta
 * 'replace', ersetzt das Label), bei abgelaufenem oder gebounctem Assessment
 * das Assessment-Label plus Aufforderung (cta 'append'), sonst nur das
 * Assessment-Label.
 */
export const getPrimaryTalentStatus = (space) => {
  const { assessment_status, smart_predict_status, progress } = space

  if (smart_predict_status?.isUserStatus) {
    return {
      status: smart_predict_status.status,
      isUserStatus: true,
      cta: null
    }
  }

  if (progress === 100 && smart_predict_status?.key === 'SMART_PREDICT') {
    return {
      status: smart_predict_status.status,
      isUserStatus: false,
      cta: 'replace'
    }
  }

  return {
    status: assessment_status?.status,
    isUserStatus: false,
    cta: FINAL_ASSESSMENT_KEYS.includes(assessment_status?.key)
      ? 'append'
      : null
  }
}

const getInitials = (name) => {
  if (!name) return ''

  return name.includes(' ') ? name : name[0]
}

const getName = (firstname, lastname) => {
  if (!firstname && !lastname) return undefined
  if (firstname && !lastname) return firstname
  if (!firstname && lastname) return lastname

  return `${firstname} ${lastname}`
}

const getScore = ({ matching_score_to_space_career }) => {
  return isNumber(matching_score_to_space_career)
    ? Math.round(matching_score_to_space_career)
    : '-'
}
